<template>
    <BaseContainer>
        <span slot="principal">
            <v-content class="pr-2 pl-2">
                <v-toolbar class="ma-2" style="border-radius: 30px;">
                    <v-btn icon link to="/">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer/>
                    <v-toolbar-title v-show="$vuetify.breakpoint.smAndUp">Clientes</v-toolbar-title>
                    <v-spacer/>
                    <v-btn icon :loading="carregando" title="Atualizar" @click="consultar">
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </v-toolbar>

                <v-card elevation="2" class="ma-1">
                    <v-card-text>
                        <!-- Cards -->
                        <v-row>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="String(resumo.ativos)"
                                    label="Ativos"
                                    color="success"
                                    icon="mdi-account-check"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="String(resumo.inativos)"
                                    label="Inativos"
                                    color="grey"
                                    icon="mdi-account-off"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="String(resumo.assinantes)"
                                    label="Assinantes"
                                    color="blue darken-1"
                                    icon="mdi-account-star"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="String(resumo.inadimplentes)"
                                    label="Inadimplentes"
                                    color="error"
                                    icon="mdi-account-alert"
                                />
                            </v-col>
                        </v-row>

                        <!-- LTV -->
                        <v-row>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="`R$ ${formatPrice(ltv.ltv_medio)}`"
                                    label="LTV Medio"
                                    color="purple accent-2"
                                    icon="mdi-diamond-stone"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="`${ltv.tempo_medio_vida_meses} meses`"
                                    label="Tempo Medio de Vida"
                                    color="teal"
                                    icon="mdi-clock-outline"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="`R$ ${formatPrice(ltv.receita_media_mensal_por_cliente)}`"
                                    label="Receita Media Mensal/Cliente"
                                    color="orange darken-1"
                                    icon="mdi-currency-usd"
                                />
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <Card
                                    :value="`R$ ${formatPrice(ltv.receita_total)}`"
                                    label="Receita Total"
                                    color="green darken-2"
                                    icon="mdi-cash-100"
                                />
                            </v-col>
                        </v-row>

                        <!-- Tabs -->
                        <v-row>
                            <v-col cols="12">
                                <v-card class="mx-auto" elevation="4" outlined>
                                    <v-tabs v-model="tab">
                                        <v-tab>Ativos por Plano</v-tab>
                                        <v-tab>Assinantes ({{ assinantes.length }})</v-tab>
                                        <v-tab>Inadimplentes ({{ inadimplentes.length }})</v-tab>
                                        <v-tab>LTV por Cliente ({{ ltvDetalhado.length }})</v-tab>
                                    </v-tabs>

                                    <v-tabs-items v-model="tab" touchless>
                                        <!-- Ativos por Plano -->
                                        <v-tab-item>
                                            <v-data-table
                                                :headers="headersPlano"
                                                :items="ativosPorPlano"
                                                :mobileBreakpoint="0"
                                                hide-default-footer
                                                :items-per-page="-1"
                                                dense
                                                class="pa-4"
                                            >
                                                <template v-slot:item.mrr="{ item }">
                                                    R$ {{ formatPrice(item.mrr) }}
                                                </template>
                                            </v-data-table>
                                        </v-tab-item>

                                        <!-- Assinantes -->
                                        <v-tab-item>
                                            <v-data-table
                                                :headers="headersAssinantes"
                                                :items="assinantes"
                                                :mobileBreakpoint="0"
                                                :items-per-page="15"
                                                dense
                                                class="pa-4"
                                            >
                                                <template v-slot:item.total_pago="{ item }">
                                                    R$ {{ formatPrice(item.total_pago) }}
                                                </template>
                                                <template v-slot:item.primeiro_pagamento="{ item }">
                                                    {{ formatDate(item.primeiro_pagamento, 'DD/MM/YYYY') }}
                                                </template>
                                                <template v-slot:item.ultimo_pagamento="{ item }">
                                                    {{ formatDate(item.ultimo_pagamento, 'DD/MM/YYYY') }}
                                                </template>
                                                <template v-slot:item.ativo="{ item }">
                                                    <v-chip :color="item.ativo ? 'success' : 'error'" x-small>
                                                        {{ item.ativo ? 'Ativo' : 'Inativo' }}
                                                    </v-chip>
                                                </template>
                                            </v-data-table>
                                        </v-tab-item>

                                        <!-- Inadimplentes -->
                                        <v-tab-item>
                                            <v-data-table
                                                :headers="headersInadimplentes"
                                                :items="inadimplentes"
                                                :mobileBreakpoint="0"
                                                :items-per-page="15"
                                                dense
                                                class="pa-4"
                                            >
                                                <template v-slot:item.valor="{ item }">
                                                    R$ {{ formatPrice(item.valor) }}
                                                </template>
                                                <template v-slot:item.vencimento="{ item }">
                                                    {{ formatDate(item.vencimento, 'DD/MM/YYYY') }}
                                                </template>
                                            </v-data-table>
                                        </v-tab-item>

                                        <!-- LTV por Cliente -->
                                        <v-tab-item>
                                            <v-data-table
                                                :headers="headersLtv"
                                                :items="ltvDetalhado"
                                                :mobileBreakpoint="0"
                                                :items-per-page="15"
                                                dense
                                                class="pa-4"
                                            >
                                                <template v-slot:item.receita_total="{ item }">
                                                    R$ {{ formatPrice(item.receita_total) }}
                                                </template>
                                                <template v-slot:item.meses_vida="{ item }">
                                                    {{ Number(item.meses_vida).toFixed(1) }}
                                                </template>
                                                <template v-slot:item.ativo="{ item }">
                                                    <v-chip :color="item.ativo ? 'success' : 'error'" x-small>
                                                        {{ item.ativo ? 'Ativo' : 'Inativo' }}
                                                    </v-chip>
                                                </template>
                                            </v-data-table>
                                        </v-tab-item>
                                    </v-tabs-items>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer.vue';
import Card from '@/views/home/Card.vue';

export default {
    name: 'RelatorioClientes',
    components: { BaseContainer, Card },

    data: () => ({
        tab: 0,
        carregando: false,
        resumo: { ativos: 0, inativos: 0, assinantes: 0, inadimplentes: 0 },
        ltv: { ltv_medio: 0, tempo_medio_vida_meses: 0, receita_media_mensal_por_cliente: 0, receita_total: 0 },
        ltvDetalhado: [],
        ativosPorPlano: [],
        assinantes: [],
        inadimplentes: [],
        headersPlano: [
            { text: 'Plano', value: 'plano_nome' },
            { text: 'Clientes', value: 'qtd_clientes' },
            { text: 'MRR', value: 'mrr' },
        ],
        headersAssinantes: [
            { text: 'ID', value: 'empresa_id' },
            { text: 'Empresa', value: 'fantasia' },
            { text: 'Status', value: 'ativo' },
            { text: 'Pagamentos', value: 'qtd_pagamentos' },
            { text: 'Total Pago', value: 'total_pago' },
            { text: 'Primeiro Pgto', value: 'primeiro_pagamento' },
            { text: 'Ultimo Pgto', value: 'ultimo_pagamento' },
        ],
        headersInadimplentes: [
            { text: 'ID', value: 'empresa_id' },
            { text: 'Empresa', value: 'fantasia' },
            { text: 'WhatsApp', value: 'whatsapp' },
            { text: 'Vencimento', value: 'vencimento' },
            { text: 'Status Cobranca', value: 'charge_status' },
            { text: 'Valor', value: 'valor' },
        ],
        headersLtv: [
            { text: 'ID', value: 'empresa_id' },
            { text: 'Empresa', value: 'fantasia' },
            { text: 'Status', value: 'ativo' },
            { text: 'Receita Total', value: 'receita_total' },
            { text: 'Meses Vida', value: 'meses_vida' },
            { text: 'Pagamentos', value: 'qtd_pagamentos' },
        ],
    }),

    mounted() {
        this.consultar();
    },

    methods: {
        async consultar() {
            this.carregando = true;

            try {
                const [resumo, ltvResp, plano, assinantes, inadimplentes] = await Promise.all([
                    this.$http.get('relatorios/clientes/resumo'),
                    this.$http.get('relatorios/clientes/ltv'),
                    this.$http.get('relatorios/clientes/ativos-por-plano'),
                    this.$http.get('relatorios/clientes/assinantes'),
                    this.$http.get('relatorios/clientes/inadimplentes'),
                ]);

                this.resumo = resumo.data.data;
                this.ltv = ltvResp.data.data;
                this.ltvDetalhado = ltvResp.data.detalhado || [];
                this.ativosPorPlano = plano.data.data;
                this.assinantes = assinantes.data.data;
                this.inadimplentes = inadimplentes.data.data;
            } catch (e) {
                console.error(e);
                this.notify('Erro ao carregar relatório de clientes', 'warning');
            } finally {
                this.carregando = false;
            }
        },
    },
};
</script>
